import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// Constants - Functions
import { protocols } from "../../../../config/constants";
import { getValidProtocols } from "../../../../config/validateProtocols";
import { GetAppBasicProtocolData } from "../getProtocolData/appBasics";
import { GetAppRotationProtocolData } from "../getProtocolData/appRotation";
import { GetAppARProtocolData } from "../getProtocolData/appAR";
// Components
import PrescriptionsHeader from "../prescriptionsHeader";
import ProtocolPrescription from "../protocolPrescription";

const AppProtocols = ({
  software,
  prescriptions,
  prescriptionsToDatabase,
  hospitalProtocols,
  protocolsSessionsNumber,
  isDisabledInputs,
  minutesUsage,
  protocolPlatforms,
  isIncompatibleWithAR,
}) => {
  const { patientId } = useParams();
  const [t] = useTranslation("global");

  const getProtocolPrescriptions = (protocolId) => {
    return prescriptions.filter((prescription) => prescription.PROTOCOL_ID === protocolId);
  };

  const renderBasicProtocols = () => {
    const basicProtocols = Object.values(protocols.app.basic);
    const orderedBasicProtocols = getValidProtocols(basicProtocols, hospitalProtocols);

    if (orderedBasicProtocols.length) {
      return (
        <>
          <PrescriptionsHeader software={software} activityType="prescriptions_basicActivities" />
          {orderedBasicProtocols.map((orderedProtocol) => {
            const protocolId = orderedProtocol.PROTOCOL_ID;
            const protocolPrescriptions = getProtocolPrescriptions(protocolId);
            const protocolSessions = protocolsSessionsNumber.filter((protocol) => protocol.PROTOCOL_ID === protocolId)[0];
            const sessionsNumber = protocolSessions ? protocolSessions.sessionsNumber : 0;
            const protocolMinutesUsage = minutesUsage.hasOwnProperty(protocolId) ? `${minutesUsage[protocolId]}%` : "-";
            const { img, name, video, description } = GetAppBasicProtocolData(protocolId);

            return (
              <ProtocolPrescription
                key={protocolId}
                protocolId={protocolId}
                protocolImg={img}
                protocolName={t(`protocols_${name}`)}
                prescriptions={protocolPrescriptions}
                prescriptionsToDatabase={prescriptionsToDatabase}
                isDisabledInputs={isDisabledInputs}
                patientId={patientId}
                software={software}
                sessionsNumber={sessionsNumber}
                minutesUsage={protocolMinutesUsage}
                videoLink={t(`protocols_${video}`)}
                protocolDescription={t(`protocols_${description}`)}
                protocolPlatforms={protocolPlatforms}
              />
            );
          })}
        </>
      );
    }
  };

  const renderRotationProtocols = () => {
    const rotationProtocols = Object.values(protocols.app.rotation);
    const orderedRotationProtocols = getValidProtocols(rotationProtocols, hospitalProtocols);

    if (orderedRotationProtocols.length) {
      return (
        <>
          <PrescriptionsHeader software={software} activityType="prescriptions_rotationActivities" />
          {orderedRotationProtocols.map((orderedProtocol) => {
            const protocolId = orderedProtocol.PROTOCOL_ID;
            const protocolPrescriptions = getProtocolPrescriptions(protocolId);
            const protocolSessions = protocolsSessionsNumber.filter((protocol) => protocol.PROTOCOL_ID === protocolId)[0];
            const sessionsNumber = protocolSessions ? protocolSessions.sessionsNumber : 0;
            const protocolMinutesUsage = minutesUsage.hasOwnProperty(protocolId) ? `${minutesUsage[protocolId]}%` : "-";
            const { img, name, video, description } = GetAppRotationProtocolData(protocolId);

            return (
              <ProtocolPrescription
                key={protocolId}
                protocolId={protocolId}
                protocolImg={img}
                protocolName={t(`protocols_${name}`)}
                prescriptions={protocolPrescriptions}
                prescriptionsToDatabase={prescriptionsToDatabase}
                isDisabledInputs={isDisabledInputs}
                patientId={patientId}
                software={software}
                sessionsNumber={sessionsNumber}
                minutesUsage={protocolMinutesUsage}
                videoLink={t(`protocols_${video}`)}
                protocolDescription={t(`protocols_${description}`)}
                protocolPlatforms={protocolPlatforms}
              />
            );
          })}
        </>
      );
    }
  };

  const renderARProtocols = () => {
    const arProtocols = Object.values(protocols.app.ar);
    const orderedARProtocols = getValidProtocols(arProtocols, hospitalProtocols);

    if (orderedARProtocols.length) {
      return (
        <>
          <PrescriptionsHeader software={software} activityType="prescriptions_arActivities" isARSection />
          {orderedARProtocols.map((orderedProtocol) => {
            const protocolId = orderedProtocol.PROTOCOL_ID;
            const protocolPrescriptions = getProtocolPrescriptions(protocolId);
            const protocolSessions = protocolsSessionsNumber.filter((protocol) => protocol.PROTOCOL_ID === protocolId)[0];
            const sessionsNumber = protocolSessions ? protocolSessions.sessionsNumber : 0;
            const protocolMinutesUsage = minutesUsage.hasOwnProperty(protocolId) ? `${minutesUsage[protocolId]}%` : "-";
            const { img, name, video, description } = GetAppARProtocolData(protocolId);

            return (
              <ProtocolPrescription
                key={protocolId}
                protocolId={protocolId}
                protocolImg={img}
                protocolName={t(`protocols_${name}`)}
                prescriptions={protocolPrescriptions}
                prescriptionsToDatabase={prescriptionsToDatabase}
                isDisabledInputs={isDisabledInputs}
                patientId={patientId}
                software={software}
                sessionsNumber={sessionsNumber}
                minutesUsage={protocolMinutesUsage}
                videoLink={t(`protocols_${video}`)}
                protocolDescription={t(`protocols_${description}`)}
                protocolPlatforms={protocolPlatforms}
                isIncompatibleWithAR={isIncompatibleWithAR}
              />
            );
          })}
        </>
      );
    }
  };

  return (
    <>
      {renderBasicProtocols()}
      {renderRotationProtocols()}
      {renderARProtocols()}
    </>
  );
};

export default AppProtocols;
